/**
 * HR Allowance Master — Phase 1B catalog, list view (Phase 3.1 redesign).
 *
 *   - Compact list with an active on/off toggle per row (some tenants
 *     don't use every preset, so they can switch off without losing it)
 *   - "เพิ่มจากรายการแนะนำ" — 10 common Thai SME allowances with a
 *     one-line tooltip; tapping adds it with sensible tax/SSO/PF flags
 *   - Custom add/edit modal for anything else
 */
import { useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import {
  useAllowanceMaster,
  useUpsertAllowanceMaster,
  useEmployees,
  useAllowanceEligibilityByAllowance,
  useUpsertAllowanceEligibility,
  useDeleteAllowanceEligibility,
  useDiligenceLadder,
  useSaveDiligenceLadder,
  type AllowanceMasterRow,
  type AllowanceEligibilityRow,
} from '../../hooks';
import { Plus, Pencil, X, Save, Info, Sparkles, Users, TrendingUp } from 'lucide-react';
import { ALLOWANCE_PRESETS, ALLOWANCE_FREQUENCIES } from '@payroll/shared';
import { useToast } from '../../components/ui/toast';

const EMPTY: Partial<AllowanceMasterRow> = {
  code: '',
  name_th: '',
  preset_type: 'custom',
  is_taxable: true,
  is_ssoable: true,
  is_pfable: true,
  frequency: 'fixed_monthly',
  default_amount: null,
  default_rate: null,
  rate_unit: null,
  active: true,
};

// 10 common Thai SME allowances offered as one-tap "suggested" adds.
// flags follow typical Thai practice: welfare (สวัสดิการ) reimbursements
// are non-taxable/non-SSO/non-PF; cash allowances are fully assessable.
interface Suggestion {
  code: string;
  name_th: string;
  preset_type: AllowanceMasterRow['preset_type'];
  is_taxable: boolean;
  is_ssoable: boolean;
  is_pfable: boolean;
  frequency: 'fixed_monthly' | 'variable';
  rate_unit?: string;
  condition_type?: 'perfect_attendance';
  tip: string;
}
const SUGGESTIONS: Suggestion[] = [
  { code: 'phone', name_th: 'ค่าโทรศัพท์', preset_type: 'phone_actual', is_taxable: true, is_ssoable: true, is_pfable: true, frequency: 'fixed_monthly', tip: 'ค่าโทรศัพท์เหมาจ่ายรายเดือน เข้าฐานภาษี/ปกส.' },
  { code: 'diligence', name_th: 'เบี้ยขยัน', preset_type: 'custom', is_taxable: true, is_ssoable: true, is_pfable: true, frequency: 'fixed_monthly', condition_type: 'perfect_attendance', tip: 'จ่ายเฉพาะเดือนที่ไม่ขาด-ลา-สายเลย (เงื่อนไขอัตโนมัติ)' },
  { code: 'shift_allowance', name_th: 'ค่ากะ', preset_type: 'custom', is_taxable: true, is_ssoable: true, is_pfable: true, frequency: 'fixed_monthly', tip: 'เงินเพิ่มสำหรับคนเข้ากะ เช่น กะบ่าย/กะดึก' },
  { code: 'fuel', name_th: 'ค่าน้ำมัน (ตามจริง)', preset_type: 'travel_actual', is_taxable: false, is_ssoable: false, is_pfable: false, frequency: 'fixed_monthly', tip: 'เบิกตามใบเสร็จจริง ไม่เข้าฐานภาษี' },
  { code: 'housing', name_th: 'ค่าที่พัก / เช่าบ้าน', preset_type: 'custom', is_taxable: true, is_ssoable: true, is_pfable: true, frequency: 'fixed_monthly', tip: 'ค่าเช่าบ้าน/หอพักรายเดือน เข้าฐานภาษี' },
  { code: 'professional', name_th: 'ค่าวิชาชีพ / ใบอนุญาต', preset_type: 'custom', is_taxable: true, is_ssoable: true, is_pfable: true, frequency: 'fixed_monthly', tip: 'เงินเพิ่มผู้มีใบอนุญาตวิชาชีพ เช่น วิศวกร พยาบาล' },
  { code: 'skill', name_th: 'ค่าทักษะ / ภาษา', preset_type: 'custom', is_taxable: true, is_ssoable: true, is_pfable: true, frequency: 'fixed_monthly', tip: 'เงินเพิ่มทักษะพิเศษ เช่น ภาษาต่างประเทศ ช่างเทคนิค' },
  { code: 'per_diem', name_th: 'เบี้ยเลี้ยงเดินทาง (ต่อเที่ยว)', preset_type: 'travel_fixed', is_taxable: true, is_ssoable: true, is_pfable: true, frequency: 'variable', rate_unit: 'เที่ยว', tip: 'จ่ายต่อเที่ยวเดินทางต่างจังหวัด — กรอกจำนวนเที่ยวตอนทำเงินเดือน' },
  { code: 'medical', name_th: 'ค่ารักษาพยาบาล (สวัสดิการ)', preset_type: 'custom', is_taxable: false, is_ssoable: false, is_pfable: false, frequency: 'fixed_monthly', tip: 'สวัสดิการค่ารักษา ไม่เข้าฐานภาษี/ปกส.' },
  { code: 'child_edu', name_th: 'ค่าเล่าเรียนบุตร (สวัสดิการ)', preset_type: 'custom', is_taxable: false, is_ssoable: false, is_pfable: false, frequency: 'fixed_monthly', tip: 'สวัสดิการช่วยค่าเล่าเรียนบุตร ไม่เข้าฐานภาษี' },
];

/** Content-only version used by the Wage hub. */
export function HRAllowanceMasterContent() {
  const { data, isLoading } = useAllowanceMaster();
  const upsert = useUpsertAllowanceMaster();
  const toast = useToast();
  const [editing, setEditing] = useState<Partial<AllowanceMasterRow> | null>(null);
  const [assigning, setAssigning] = useState<AllowanceMasterRow | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const existingCodes = new Set((data ?? []).map((r) => r.code));
  const availableSuggestions = SUGGESTIONS.filter((s) => !existingCodes.has(s.code));

  async function handleSave() {
    if (!editing?.code || !editing.name_th) { toast.error('กรอก code + ชื่อภาษาไทย'); return; }
    try {
      await upsert.mutateAsync(editing);
      setEditing(null);
    } catch (e) { toast.error(`บันทึกไม่สำเร็จ: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'}`); }
  }

  async function toggleActive(row: AllowanceMasterRow) {
    try {
      await upsert.mutateAsync({ ...row, active: !row.active });
    } catch (e) { toast.error(`สลับสถานะไม่สำเร็จ: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'}`); }
  }

  async function addSuggestion(s: Suggestion) {
    try {
      await upsert.mutateAsync({
        code: s.code,
        name_th: s.name_th,
        preset_type: s.preset_type,
        is_taxable: s.is_taxable,
        is_ssoable: s.is_ssoable,
        is_pfable: s.is_pfable,
        frequency: s.frequency,
        rate_unit: s.rate_unit ?? null,
        condition_type: s.condition_type ?? null,
        active: true,
      });
    } catch (e) { toast.error(`เพิ่มไม่สำเร็จ: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'}`); }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <p className="text-sm text-gray-600">คลังแม่แบบเงินเพิ่ม — เปิด/ปิดได้ตามที่บริษัทใช้จริง</p>
        <div className="flex items-center gap-1.5 shrink-0">
          <button onClick={() => setShowSuggestions((v) => !v)} className="btn-secondary btn-slim">
            <Sparkles className="w-3.5 h-3.5" /> รายการแนะนำ
          </button>
          <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary btn-slim">
            <Plus className="w-3.5 h-3.5" /> เพิ่มเอง
          </button>
        </div>
      </div>
      {/* Suggested presets */}
      {showSuggestions && (
        <div className="card p-3 mb-4 bg-brand-50/40 border-brand-200">
          <div className="text-sm font-semibold mb-2 flex items-center gap-1">
            <Sparkles className="w-4 h-4 text-brand-600" /> รายการที่นิยมใช้ — แตะเพื่อเพิ่ม
          </div>
          {availableSuggestions.length === 0 ? (
            <p className="text-xs text-gray-500">เพิ่มครบทุกรายการแนะนำแล้ว 🎉</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {availableSuggestions.map((s) => (
                <button
                  key={s.code}
                  onClick={() => addSuggestion(s)}
                  title={s.tip}
                  className="text-xs border border-brand-300 bg-white rounded-full px-3 py-1.5 hover:bg-brand-50 transition flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> {s.name_th}
                  {!s.is_taxable && <span className="text-green-600 text-[10px]">(สวัสดิการ)</span>}
                </button>
              ))}
            </div>
          )}
          <p className="text-[10px] text-gray-500 mt-2">
            💡 ชี้ค้าง (hover) ที่ปุ่มเพื่อดูคำอธิบายว่าแต่ละรายการคืออะไร
          </p>
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <p className="py-8 text-center text-gray-500">กำลังโหลด…</p>
      ) : (data ?? []).length === 0 ? (
        <p className="py-8 text-center text-gray-500">ยังไม่มีรายการ — กด "รายการแนะนำ" หรือ "เพิ่มเอง"</p>
      ) : (
        <div className="card divide-y divide-gray-100">
          {(data ?? []).map((row) => (
            <div key={row.id} className={`flex items-center gap-3 p-3 ${row.active ? '' : 'opacity-50'}`}>
              {/* Toggle */}
              <button
                onClick={() => toggleActive(row)}
                title={row.active ? 'กดเพื่อปิดใช้งาน' : 'กดเพื่อเปิดใช้งาน'}
                className={`relative w-10 h-6 rounded-full transition shrink-0 ${row.active ? 'bg-brand-600' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${row.active ? 'left-[18px]' : 'left-0.5'}`} />
              </button>

              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm flex items-center gap-1.5">
                  {row.name_th}
                  <span className="text-[10px] text-gray-400 font-mono">{row.code}</span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
                  <span>
                    {row.frequency === 'fixed_monthly'
                      ? `เหมาจ่าย${row.default_amount ? ` ${row.default_amount.toLocaleString('th-TH')} ฿` : ''}`
                      : `ตามจริง${row.default_rate ? ` ${row.default_rate}/${row.rate_unit ?? 'หน่วย'}` : ' (กรอกจำนวนตอนทำเงินเดือน)'}`}
                  </span>
                  <span className="flex gap-1">
                    {row.is_taxable
                      ? <span className="badge bg-blue-50 text-blue-700 text-[10px]">เข้าภาษี</span>
                      : <span className="badge bg-green-50 text-green-700 text-[10px]">ไม่เข้าภาษี</span>}
                    {row.is_ssoable && <span className="badge bg-purple-50 text-purple-700 text-[10px]">ปกส.</span>}
                    {row.is_pfable && <span className="badge bg-amber-50 text-amber-700 text-[10px]">PF</span>}
                  </span>
                </div>
              </div>

              <button onClick={() => setAssigning(row)} className="btn-secondary btn-slim shrink-0">
                <Users className="w-3.5 h-3.5" /> พนักงาน
              </button>
              <button onClick={() => setEditing(row)} className="p-1 text-gray-400 hover:text-brand-600 shrink-0">
                <Pencil className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end lg:items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-2xl p-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-3">
              <h2 className="font-semibold">{editing.id ? 'แก้ไข' : 'เพิ่ม'} เงินเพิ่ม</h2>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="label flex items-center gap-1">
                    Code *
                    <Info className="w-3 h-3 text-gray-400" />
                    <span className="sr-only">รหัสภายในระบบ ห้ามซ้ำ</span>
                  </label>
                  <input className="input font-mono" value={editing.code ?? ''} onChange={(e) => setEditing({ ...editing, code: e.target.value })} disabled={!!editing.id} />
                </div>
                <div>
                  <label className="label">ประเภท</label>
                  <select
                    className="input"
                    value={editing.preset_type ?? 'custom'}
                    onChange={(e) => setEditing({ ...editing, preset_type: e.target.value as AllowanceMasterRow['preset_type'] })}
                  >
                    {ALLOWANCE_PRESETS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="label">ชื่อ (ภาษาไทย) *</label>
                <input className="input" value={editing.name_th ?? ''} onChange={(e) => setEditing({ ...editing, name_th: e.target.value })} />
              </div>
              <div>
                <label className="label">วิธีจ่าย</label>
                <select
                  className="input"
                  value={editing.frequency ?? 'fixed_monthly'}
                  onChange={(e) => setEditing({ ...editing, frequency: e.target.value as 'fixed_monthly' | 'variable' })}
                >
                  {ALLOWANCE_FREQUENCIES.map((f) => (
                    <option key={f} value={f}>{f === 'fixed_monthly' ? 'เหมาจ่ายรายเดือน' : 'ตามจริง (กรอกจำนวนตอนทำเงินเดือน)'}</option>
                  ))}
                </select>
              </div>
              {editing.frequency === 'fixed_monthly' ? (
                <div>
                  <label className="label">จำนวนเริ่มต้น (บาท)</label>
                  <input type="number" className="input" value={editing.default_amount ?? ''} onChange={(e) => setEditing({ ...editing, default_amount: e.target.value ? Number(e.target.value) : null })} />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="label">เรต (บาท/หน่วย)</label><input type="number" className="input" value={editing.default_rate ?? ''} onChange={(e) => setEditing({ ...editing, default_rate: e.target.value ? Number(e.target.value) : null })} /></div>
                  <div><label className="label">หน่วย</label><input className="input" placeholder="เที่ยว / วัน / กม." value={editing.rate_unit ?? ''} onChange={(e) => setEditing({ ...editing, rate_unit: e.target.value })} /></div>
                </div>
              )}
              <div className="space-y-1 bg-gray-50 rounded p-2">
                <label className="text-xs text-gray-500 uppercase">นับรวมในฐานคำนวณของ:</label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!editing.is_taxable} onChange={(e) => setEditing({ ...editing, is_taxable: e.target.checked })} />
                  ภาษี — ติ๊กถ้าเป็นเงินสด (สวัสดิการเบิกตามจริงไม่ต้องติ๊ก)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!editing.is_ssoable} onChange={(e) => setEditing({ ...editing, is_ssoable: e.target.checked })} />
                  ประกันสังคม (ปกส.)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!editing.is_pfable} onChange={(e) => setEditing({ ...editing, is_pfable: e.target.checked })} />
                  กองทุนสำรองเลี้ยงชีพ (PF)
                </label>
              </div>
              <label className="flex items-center gap-2 text-sm bg-amber-50 rounded p-2">
                <input type="checkbox" checked={editing.condition_type === 'perfect_attendance'}
                  onChange={(e) => setEditing({ ...editing, condition_type: e.target.checked ? 'perfect_attendance' : null })} />
                จ่ายเฉพาะเดือนที่ไม่ขาด/ลา/มาสายเลย (เบี้ยขยัน)
              </label>
              <button onClick={handleSave} disabled={upsert.isPending} className="btn-primary w-full">
                <Save className="w-4 h-4" /> {upsert.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
              </button>
            </div>
          </div>
        </div>
      )}

      {assigning && <AssignAllowanceModal allowance={assigning} onClose={() => setAssigning(null)} />}

      <DiligenceLadderConfig />
    </div>
  );
}

const DILIGENCE_LEAVE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'sick', label: 'ลาป่วย' },
  { value: 'personal', label: 'ลากิจ' },
  { value: 'vacation', label: 'ลาพักร้อน' },
  { value: 'unpaid', label: 'ลาไม่รับค่าจ้าง' },
  { value: 'maternity', label: 'ลาคลอด' },
];

/**
 * เบี้ยขยันขั้นบันได — ตั้งขั้นบันได + เงื่อนไขเลื่อน/ลดขั้น. เดือนสะอาด → ขึ้น 1 ขั้น,
 * มีสาย/ขาด/ลา(ที่เลือก) → ลง 1 ขั้น (ไม่ต่ำกว่าขั้น 1). คิดตอนทำเงินเดือนอัตโนมัติ.
 */
function DiligenceLadderConfig() {
  const { data: cfg, isLoading } = useDiligenceLadder();
  const save = useSaveDiligenceLadder();
  const toast = useToast();

  const [active, setActive] = useState(false);
  const [steps, setSteps] = useState<string[]>(['300', '500', '700', '1000']);
  const [startTier, setStartTier] = useState('1');
  const [breakLate, setBreakLate] = useState(true);
  const [breakAbsent, setBreakAbsent] = useState(true);
  const [breakLeaves, setBreakLeaves] = useState<string[]>(['unpaid', 'personal']);
  const [resetOnBreak, setResetOnBreak] = useState(false);
  const [earlyMin, setEarlyMin] = useState('0');
  const [taxable, setTaxable] = useState(true);
  const [ssoable, setSsoable] = useState(true);
  const [pfable, setPfable] = useState(true);
  const [loaded, setLoaded] = useState(false);

  // hydrate ครั้งเดียวจากค่าที่บันทึกไว้
  if (cfg && !loaded) {
    setActive(cfg.active);
    if (cfg.steps?.length) setSteps(cfg.steps.map(String));
    setStartTier(String(cfg.start_tier ?? 1));
    setBreakLate(cfg.break_on_late);
    setBreakAbsent(cfg.break_on_absent);
    setBreakLeaves(cfg.break_leave_types ?? []);
    setResetOnBreak(cfg.reset_on_break ?? false);
    setEarlyMin(String(cfg.require_early_minutes ?? 0));
    setTaxable(cfg.is_taxable);
    setSsoable(cfg.is_ssoable);
    setPfable(cfg.is_pfable);
    setLoaded(true);
  }

  function toggleLeave(v: string) {
    setBreakLeaves((p) => (p.includes(v) ? p.filter((x) => x !== v) : [...p, v]));
  }
  function setStep(i: number, v: string) {
    setSteps((p) => p.map((s, idx) => (idx === i ? v : s)));
  }

  function handleSave() {
    const parsedSteps = steps.map((s) => Number(s)).filter((n) => Number.isFinite(n) && n >= 0);
    if (active && parsedSteps.length === 0) { toast.error('กรอกอย่างน้อย 1 ขั้น'); return; }
    save.mutate(
      {
        active,
        steps: parsedSteps,
        start_tier: Math.max(1, Number(startTier) || 1),
        break_on_late: breakLate,
        break_on_absent: breakAbsent,
        break_leave_types: breakLeaves,
        reset_on_break: resetOnBreak,
        require_early_minutes: Math.max(0, Number(earlyMin) || 0),
        is_taxable: taxable, is_ssoable: ssoable, is_pfable: pfable,
      },
      {
        onSuccess: () => toast.success('บันทึกเบี้ยขยันขั้นบันไดแล้ว'),
        onError: (e) => toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'),
      },
    );
  }

  if (isLoading) return null;

  return (
    <div className="card p-4 mt-6 border-teal-200 bg-teal-50/40">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h3 className="font-semibold flex items-center gap-2"><TrendingUp className="w-4 h-4 text-teal-600" /> เบี้ยขยันขั้นบันได</h3>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> เปิดใช้
        </label>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        เดือนที่ไม่สาย/ขาด/ลา(ที่กำหนด) → เลื่อนขึ้น 1 ขั้น · เดือนที่มี → ตามโหมดด้านล่าง. คิดตอนทำเงินเดือนอัตโนมัติ
      </p>

      {active && (
        <div className="space-y-3">
          <div>
            <label className="label">ขั้นบันได (บาท/เดือน) — เรียงจากขั้นต่ำสุดไปสูงสุด</label>
            <div className="flex flex-wrap gap-2">
              {steps.map((s, i) => (
                <div key={i} className="flex items-center gap-1">
                  <span className="text-xs text-gray-400">ขั้น {i + 1}</span>
                  <input type="number" className="input w-24" value={s} onChange={(e) => setStep(i, e.target.value)} />
                  {steps.length > 1 && (
                    <button onClick={() => setSteps((p) => p.filter((_, idx) => idx !== i))} className="text-red-500"><X className="w-4 h-4" /></button>
                  )}
                </div>
              ))}
              <button onClick={() => setSteps((p) => [...p, ''])} className="btn-secondary text-sm"><Plus className="w-4 h-4" /> เพิ่มขั้น</button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label">ขั้นเริ่มต้น (พนักงานใหม่)</label>
              <input type="number" className="input" value={startTier} onChange={(e) => setStartTier(e.target.value)} disabled={resetOnBreak} />
              {resetOnBreak && <p className="text-[11px] text-gray-400 mt-0.5">โหมดรีเซ็ตเริ่มที่ขั้น 1 เสมอ</p>}
            </div>
            <div>
              <label className="label">ต้องเข้าก่อนกะ (นาที)</label>
              <input type="number" min="0" className="input" value={earlyMin} onChange={(e) => setEarlyMin(e.target.value)} placeholder="0 = ไม่บังคับ" />
              <p className="text-[11px] text-gray-400 mt-0.5">เช่น 10 = ต้องเข้าก่อนเวลากะ ≥ 10 นาทีทุกวัน</p>
            </div>
          </div>

          <div className="bg-white rounded p-2">
            <label className="text-xs text-gray-500 uppercase">เมื่อมีปัญหา (สาย/ขาด/ลา)</label>
            <label className="flex items-start gap-2 text-sm mt-1">
              <input type="checkbox" className="mt-0.5" checked={resetOnBreak} onChange={(e) => setResetOnBreak(e.target.checked)} />
              <span>เด้งกลับขั้น 1 + เดือนนั้นได้ <strong>0 บาท</strong> (เริ่มนับใหม่)
                <span className="block text-[11px] text-gray-400">ไม่ติ๊ก = ลดทีละ 1 ขั้น ไม่ต่ำกว่าขั้น 1 (ยังได้เบี้ยขั้นต่ำ)</span>
              </span>
            </label>
          </div>

          <div className="bg-white rounded p-2 space-y-1">
            <label className="text-xs text-gray-500 uppercase">สิ่งที่ทำให้ลด/รีเซ็ตขั้น</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={breakLate} onChange={(e) => setBreakLate(e.target.checked)} /> มาสาย</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={breakAbsent} onChange={(e) => setBreakAbsent(e.target.checked)} /> ขาดงาน</label>
            <div className="pt-1">
              <span className="text-xs text-gray-500">ลาที่ทำให้ลดขั้น:</span>
              <div className="flex flex-wrap gap-2 mt-1">
                {DILIGENCE_LEAVE_OPTIONS.map((o) => (
                  <label key={o.value} className={`text-xs px-2 py-1 rounded-full border cursor-pointer ${breakLeaves.includes(o.value) ? 'bg-teal-100 border-teal-400 text-teal-800' : 'bg-white border-gray-200 text-gray-500'}`}>
                    <input type="checkbox" className="hidden" checked={breakLeaves.includes(o.value)} onChange={() => toggleLeave(o.value)} />
                    {o.label}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white rounded p-2 space-y-1">
            <label className="text-xs text-gray-500 uppercase">นับรวมในฐานคำนวณของ:</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={taxable} onChange={(e) => setTaxable(e.target.checked)} /> ภาษี</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={ssoable} onChange={(e) => setSsoable(e.target.checked)} /> ประกันสังคม</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={pfable} onChange={(e) => setPfable(e.target.checked)} /> กองทุนสำรองเลี้ยงชีพ</label>
          </div>
        </div>
      )}

      <button onClick={handleSave} disabled={save.isPending} className="btn-primary w-full mt-3">
        <Save className="w-4 h-4" /> {save.isPending ? 'กำลังบันทึก…' : 'บันทึกเบี้ยขยันขั้นบันได'}
      </button>
    </div>
  );
}

/** Standalone page kept for legacy /hr/allowance-master (now redirects via Wage hub). */
export function HRAllowanceMaster() {
  return (
    <AdminLayout title="เงินเพิ่ม / เบี้ยเลี้ยง">
      <HRAllowanceMasterContent />
    </AdminLayout>
  );
}

// === Assign employees to an allowance, with per-person amount override ===
type EmpLite = { emp_code: string; first_name: string; last_name: string; nickname?: string | null };
function AssignAllowanceModal({ allowance, onClose }: { allowance: AllowanceMasterRow; onClose: () => void }) {
  const { data: employees } = useEmployees() as { data: EmpLite[] | undefined };
  const { data: elig } = useAllowanceEligibilityByAllowance(allowance.id);
  const upsert = useUpsertAllowanceEligibility();
  const remove = useDeleteAllowanceEligibility();
  const isVariable = allowance.frequency === 'variable';
  const defAmt = isVariable ? allowance.default_rate : allowance.default_amount;
  const fmt = (n: number) => n.toLocaleString('th-TH', { maximumFractionDigits: 2 });
  const startIso = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-01`;

  const byEmp = new Map<string, AllowanceEligibilityRow>();
  for (const e of (elig ?? [])) if (e.active) byEmp.set(e.emp_code, e);

  async function toggle(emp_code: string, on: boolean) {
    const ex = byEmp.get(emp_code);
    if (on && !ex) await upsert.mutateAsync({ emp_code, allowance_id: allowance.id, start_date: startIso, active: true });
    else if (!on && ex) await remove.mutateAsync(ex.id);
  }
  async function setAmount(row: AllowanceEligibilityRow, value: string) {
    const v = value.trim() === '' ? null : Number(value);
    await upsert.mutateAsync({ id: row.id, emp_code: row.emp_code, allowance_id: allowance.id, start_date: row.start_date, active: true, ...(isVariable ? { rate: v } : { fixed_amount: v }) });
  }

  const list = (employees ?? []).filter((e) => e.emp_code);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end lg:items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl p-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-1">
          <h2 className="font-semibold">เลือกพนักงาน</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-gray-500 mb-3">
          {allowance.name_th}
          {defAmt != null && <> · ค่าเริ่มต้น {fmt(defAmt)}{isVariable ? `/${allowance.rate_unit ?? 'หน่วย'}` : ' บาท/เดือน'}</>}
        </p>
        <div className="space-y-1">
          {list.map((e) => {
            const row = byEmp.get(e.emp_code);
            const checked = !!row;
            return (
              <div key={e.emp_code} className={`flex items-center gap-2 rounded-lg px-2 py-1.5 ${checked ? 'bg-brand-50' : ''}`}>
                <input type="checkbox" checked={checked} onChange={(ev) => toggle(e.emp_code, ev.target.checked)} className="w-4 h-4 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">{e.first_name} {e.last_name}{e.nickname ? ` (${e.nickname})` : ''}</div>
                  <div className="text-[11px] text-gray-400 font-mono">{e.emp_code}</div>
                </div>
                {checked && row && (
                  <input type="number" step="0.01" defaultValue={(isVariable ? row.rate : row.fixed_amount) ?? ''}
                    placeholder={defAmt != null ? fmt(defAmt) : 'จำนวน'}
                    onBlur={(ev) => setAmount(row, ev.target.value)}
                    className="w-24 rounded-lg border border-gray-300 px-2 py-1 text-xs text-right" />
                )}
              </div>
            );
          })}
          {list.length === 0 && <p className="text-center text-gray-400 py-6 text-sm">ยังไม่มีพนักงาน</p>}
        </div>
        <p className="text-[11px] text-gray-400 mt-3">เว้นช่องจำนวนว่าง = ใช้ค่าเริ่มต้น · ติ๊กออก = นำออกจากรายการนี้</p>
      </div>
    </div>
  );
}
